import { Stack, Title, useMantineTheme } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { isAddress } from 'viem'

import Loading from '@/components/Loading'
import ImageWrapper from '@/components/MediaWrappers/ImageWrapper'
import useProfileDetails from '@/hooks/useProfileDetails'
import useProfilePath from '@/hooks/useProfilePath'
import { shortenHash } from '@/utils/utils'
import utils from '@/utils/utils.module.css'

import RootButton from '../Buttons/RootButton'
import classes from '../Details/UserDetails/UserDetails.module.css'
import FlagContentModal from '../Modals/Content/FlagContentModal'
import ModalSkeleton from '../Modals/ModalSkeleton'
import profileClasses from './ProfilesCard.module.css'

interface ProfilesCardProps {
  imageSize?: number
  parentRef?: React.RefObject<HTMLDivElement>
  profileAddress?: string
  extraInfo?: JSX.Element
  isAdmin?: boolean
  triggerRefetch?: () => void
  isFlagged?: boolean
}

const ProfilesCard: React.FC<ProfilesCardProps> = ({
  imageSize = 80,
  parentRef,
  profileAddress = '',
  extraInfo,
  isAdmin = false,
  triggerRefetch = () => {},
  isFlagged = false,
}) => {
  const navigate = useNavigate()
  const theme = useMantineTheme()
  const profilePath = useProfilePath(profileAddress as `0x${string}`)
  const { t } = useTranslation()
  const [opened, { open, close }] = useDisclosure(false)

  const { creatorName, creatorUsername, creatorPfp, creatorBanner } =
    useProfileDetails(profileAddress)

  const [isHovered, setIsHovered] = useState(false)
  const handleNavigation = useCallback(() => {
    if (!opened) {
      navigate(profilePath)
    }
  }, [opened, profilePath])

  const [parentWidth, setParentWidth] = useState(imageSize)

  useEffect(() => {
    const handleWindowResize = () => {
      setParentWidth(parentRef?.current?.clientWidth || imageSize)
    }
    handleWindowResize()

    window.addEventListener('resize', handleWindowResize)

    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [])

  const handleClose = useCallback(() => {
    close()
    triggerRefetch()
    setIsHovered(false)
  }, [close, triggerRefetch])

  return (
    <Stack
      style={{
        gap: '15px',
        cursor: 'pointer',
        backgroundColor: isHovered ? theme.colors.background2[0] : '',
        border: isHovered ? `2px solid ${theme.colors.profileOutline[0]}` : '2px solid transparent',
        borderRadius: '14px',
        transition: 'background-color 0.3s',
        paddingBottom: '10px',
      }}
      onClick={handleNavigation}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Stack
        style={{
          position: 'relative',
          width: '100%',
          height: '200px',
        }}
        ref={parentRef}
      >
        {isAdmin && isHovered && (
          <RootButton
            style1={false}
            error
            style={{
              position: 'absolute',
              top: '10px',
              left: '10px',
              zIndex: 1,
            }}
            onClick={e => {
              e.stopPropagation()
              open()
            }}
          >
            {isFlagged ? t('common.unflag', 'Unflag') : t('common.flag', 'Flag')}
          </RootButton>
        )}
        <>
          {creatorBanner ? (
            <ImageWrapper
              src={creatorBanner}
              alt={creatorBanner}
              style={{
                width: parentWidth,
                height: '150px',
                objectFit: 'cover',
                objectPosition: 'center',
                border: `2px solid ${theme.colors.profileOutline[0]}`,
                borderRadius: '12px',
              }}
              width={parentWidth}
              height="150px"
            />
          ) : (
            <Loading size={`${imageSize}px`} />
          )}
        </>
        {creatorPfp ? (
          <ImageWrapper
            src={creatorPfp}
            alt={creatorPfp}
            className={`${profileClasses.imageWrapper} ${isHovered ? profileClasses.imageWrapperHover : ''}`}
            style={{
              width: `${imageSize}px`,
              height: `${imageSize}px`,
              position: 'absolute',
              bottom: 0,
              left: '10px',
              border: isHovered
                ? `2px solid ${theme.colors.textAndIcon[0]}`
                : `2px solid ${theme.colors.profileOutline[0]}`,
              borderRadius: '10px',
            }}
            width={imageSize}
            height={imageSize}
          />
        ) : (
          <Loading size={`${imageSize}px`} />
        )}
      </Stack>

      {profileAddress && (
        <Stack
          style={{
            alignSelf: 'left',
            gap: 0,
            width: parentWidth - 10,
            paddingLeft: '10px',
          }}
        >
          <Title order={4} className={utils.truncate}>
            {creatorName}
          </Title>
          {creatorUsername ? (
            <Link
              className={classes.user}
              to={profilePath}
              target="_self"
              rel="noopener noreferrer"
            >
              {isAddress(creatorUsername) ? shortenHash(creatorUsername) : creatorUsername}
            </Link>
          ) : (
            <Link
              className={classes.user}
              to={profilePath}
              target="_self"
              rel="noopener noreferrer"
            >
              {shortenHash(profileAddress)}
            </Link>
          )}
          {extraInfo}
        </Stack>
      )}

      {isAdmin && (
        <ModalSkeleton
          opened={opened}
          onClose={handleClose}
          title={
            isFlagged
              ? t('components.modals.flagContent.unflagTitle', 'Unflag Content')
              : t('components.modals.flagContent.flagTitle', 'Flag Content')
          }
        >
          <FlagContentModal
            close={handleClose}
            contentType="creators"
            contentId={profileAddress}
            isFlagged={isFlagged}
          />
        </ModalSkeleton>
      )}
    </Stack>
  )
}

export default ProfilesCard
